import { useCallback, useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import {
  CheckIcon,
  CircleAlertIcon,
  CircleQuestionMarkIcon,
  EyeIcon,
  EyeOffIcon,
  LogOutIcon,
  MonitorIcon,
  SmartphoneIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { OpenAPI } from "simplydo/interfaces";
import { MSTeamsIcon } from "simplydo/react/ui/brand-icon";
import { Button } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { Divider } from "simplydo/react/ui/divider";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { updateCurrentUser, useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import PasswordGuidance from "components/lib/Entry/PasswordGuidance";
import { MfaSettingsSection } from "components/lib/Mfa/MfaSettingsSection";

type Token = NonNullable<
  OpenAPI.GET<"/accounts/api/tokens">["response"]["tokens"]
>[number];

export const UserSettingsSecurity = () => {
  const user = useCurrentUser();
  const { t } = useTranslation();

  const [emailError, setEmailError] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [working, setWorking] = useState(false);
  const [showPasswordOld, setShowPasswordOld] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const mfaEnforced = Boolean(user?.ownerOrganisation?.mfaEnforcing);
  const [loggingOutOthers, setLoggingOutOthers] = useState(false);
  const [apiTokens, setApiTokens] = useState<Token[]>([]);
  const [loggingOutToken, setLogginOutToken] = useState("");

  useEffect(() => {
    api.auth.getApiTokens(
      (newTokens) => {
        setApiTokens(newTokens.tokens);
      },
      () => {},
    );
  }, []);

  const saveEmail = useCallback(() => {
    setEmailError("");
    setEmailLoading(true);
    api.auth.updateEmailAddress(
      { email: newEmail, sendComms: true },
      ({ email }) => {
        setEmailLoading(false);
        setNewEmail("");
        updateCurrentUser({ emails: [{ address: email, verified: false }] });
        toast("Email address successfully updated");
      },
      (err) => {
        setEmailLoading(false);
        setEmailError(err.message);
      },
    );
  }, [newEmail]);

  const updatePassword = () => {
    setWorking(true);
    api.auth.updatePassword(
      currentPassword,
      newPassword,
      () => {
        toast.success(t("users.account.options.changePassword.success"));
        setCurrentPassword("");
        setNewPassword("");
        setWorking(false);
        setErrorMessage("");
      },
      (err) => {
        setWorking(false);
        setErrorMessage(err.message);
      },
    );
  };

  const logoutToken = (tokenId: string, index: number) => {
    util
      .confirm(
        "Log out this device?",
        "To access your account again on this device you will need to re-authenticate.",
      )
      .then(() => {
        setLogginOutToken(tokenId);
        api.auth.deleteApiToken(
          tokenId,
          index,
          () => {
            setLogginOutToken("");
            if (tokenId) {
              setApiTokens(
                apiTokens.filter((tokenData) => tokenData.id !== tokenId),
              );
            } else {
              const newTokens = [...apiTokens];
              newTokens.splice(index, 1);
              setApiTokens(newTokens);
            }
          },
          (err) => {
            setLogginOutToken("");
            toast.error(err.message);
          },
        );
      })
      .catch(() => {});
  };

  const logoutOthers = () => {
    setLoggingOutOthers(true);
    util
      .confirm(
        "Log out all other devices?",
        "To access your account again on those devices you will need to re-authenticate.",
      )
      .then(() => {
        api.auth.logoutOthers(
          () => {
            setLoggingOutOthers(false);
            setApiTokens(
              apiTokens.filter((tokenData) => tokenData.token === api.token),
            );
            toast(t("users.account.options.logOut.success"));
          },
          (err) => {
            toast.error(err.message);
            setLoggingOutOthers(false);
          },
        );
      })
      .catch(() => {
        setLoggingOutOthers(false);
      });
  };

  if (!user) return null;

  return (
    <Container>
      <H3>
        {t("users.profile.communication.email.title")}
        <Subheader>{t("users.profile.communication.email.info2")}</Subheader>
      </H3>

      {user.ownerOrganisation?.preventEmailChange ? (
        <Message>
          Your avatar was automatically set from your single sign-on account.
        </Message>
      ) : null}
      {user?.ssoUser ? (
        <Message>
          Your email address was automatically set from your single sign-on
          account.
        </Message>
      ) : null}

      {emailError ? <Message color="orange">{emailError}</Message> : null}
      <FormField
        disabled={user.ownerOrganisation?.preventEmailChange || user?.ssoUser}
      >
        <FormLabel htmlFor="current-email-input">
          {t("users.profile.communication.email.current")}
        </FormLabel>
        <Input
          fluid
          id="current-email-input"
          style={{ width: 300 }}
          value={user?.emails[0]?.address}
          readOnly
        />
      </FormField>
      {user.ownerOrganisation?.preventEmailChange || user?.ssoUser ? null : (
        <>
          <FormField>
            <FormLabel htmlFor="new-email-input">
              {t("users.profile.communication.email.new")}
            </FormLabel>
            <Input
              fluid
              id="new-email-input"
              style={{ width: 300 }}
              value={newEmail}
              placeholder={t("placeholder.email")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewEmail(e.target.value)
              }
            />
          </FormField>
          <div>
            <Button loading={emailLoading} onClick={saveEmail} color="primary">
              {t("users.profile.communication.email.change.button")}
            </Button>
          </div>
        </>
      )}
      <Divider />

      {!user.ssoUser ? (
        <>
          <H3>
            {t("users.account.options.changePassword.title")}
            <Subheader>
              {t("users.account.options.changePassword.info")}
            </Subheader>
          </H3>
          <PasswordGuidance password={newPassword} />
          <FormField>
            <FormLabel htmlFor="current-password-input">
              {t("users.account.options.changePassword.currentPass")}
            </FormLabel>
            <Input
              fluid
              id="current-password-input"
              style={{ width: 300 }}
              type={showPasswordOld ? "text" : "password"}
              value={currentPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCurrentPassword(e.target.value)
              }
              icon={
                <Icon
                  icon={showPasswordOld ? EyeOffIcon : EyeIcon}
                  onClick={() => setShowPasswordOld(!showPasswordOld)}
                />
              }
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="new-password-input">
              {t("users.account.options.changePassword.newPass")}
            </FormLabel>
            <Input
              fluid
              id="new-password-input"
              style={{ width: 300 }}
              type={showPasswordNew ? "text" : "password"}
              value={newPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewPassword(e.target.value)
              }
              icon={
                <Icon
                  icon={showPasswordNew ? EyeOffIcon : EyeIcon}
                  onClick={() => setShowPasswordNew(!showPasswordNew)}
                />
              }
            />
          </FormField>
          {errorMessage && !working ? (
            <Message
              style={{ width: "50%", marginBottom: 0 }}
              size="small"
              color="red"
            >
              <Icon icon={CircleAlertIcon} />
              <MessageHeader>{errorMessage}.</MessageHeader>
            </Message>
          ) : null}
          <Button
            style={{ marginTop: "15px", maxWidth: "fit-content" }}
            onClick={() => updatePassword()}
            loading={working}
            color="primary"
          >
            <Icon icon={CheckIcon} />
            {t("users.account.options.changePassword.change")}
          </Button>

          <Divider />
          <H3>
            {t("entry.mfa.title")}
            <Subheader>
              <p>{t("entry.mfa.settingsIntro")}</p>
              <p>{t("entry.mfa.settingsIntroDetail")}</p>
            </Subheader>
          </H3>
          <MfaSettingsSection mfaEnforced={mfaEnforced} />
          <Divider />
        </>
      ) : null}

      <H3>
        Logged-in devices
        <Subheader>
          A list of all your currently active sessions across devices. You can
          revoke access here, which logs the device out immediately.
        </Subheader>
      </H3>
      {apiTokens.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell></TableHeaderCell>
              <TableHeaderCell>OS</TableHeaderCell>
              <TableHeaderCell>Browser / Device</TableHeaderCell>
              <TableHeaderCell>Created</TableHeaderCell>
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiTokens.map((apiToken: Token, index: number) => {
              const unwrappedToken = (
                (apiToken as Token) ? apiToken : { token: apiToken }
              ) as Token;
              return (
                <TableRow key={apiToken?.token}>
                  <TableCell collapsing>
                    <div>
                      {unwrappedToken.platform === "web" ? (
                        <Icon icon={MonitorIcon} size="large" />
                      ) : null}
                      {unwrappedToken.platform === "mobile" ? (
                        <Icon icon={SmartphoneIcon} size="large" />
                      ) : null}
                      {unwrappedToken.platform === "teams" ? (
                        <Icon icon={MSTeamsIcon} size="large" />
                      ) : null}
                      {!unwrappedToken.platform ? (
                        <Icon icon={CircleQuestionMarkIcon} size="large" />
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell collapsing>
                    {unwrappedToken.os || "Unknown"}
                  </TableCell>
                  <TableCell collapsing>
                    {unwrappedToken.client || "Unknown"}
                  </TableCell>
                  <TableCell collapsing>
                    {unwrappedToken.createdAt
                      ? formatDate(unwrappedToken.createdAt)
                      : "Unknown"}
                  </TableCell>
                  <TableCell collapsing textAlign="right">
                    {unwrappedToken.token === api.token ? (
                      <Label>This device</Label>
                    ) : null}
                    <Button
                      size="small"
                      onClick={() => {
                        logoutToken(unwrappedToken.id!, index);
                      }}
                      loading={loggingOutToken === unwrappedToken.id}
                      disabled={unwrappedToken.token === api.token}
                      variant="outline"
                    >
                      Log out
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : null}

      <H3>
        {t("users.account.options.logOut.title")}
        <Subheader>{t("users.account.options.logOut.info")}</Subheader>
      </H3>
      <Button
        onClick={logoutOthers}
        loading={loggingOutOthers}
        variant="outline"
      >
        <Icon icon={LogOutIcon} />
        {t("users.account.options.logOut.button")}
      </Button>
    </Container>
  );
};

export default UserSettingsSecurity;
